import React from 'react';
import classnames from 'classnames';
import { history } from 'umi';
import Iconfont from '@gp/components/Iconfont';
import GeneralImageNew from '@gp/components/GeneralImageNew';
import LinkJump from '@gp/components/LinkJump';
import { queryJumpable } from '@gp/utils/common';
import { MENU_ID_MAP, SLASH_G_POP, CONSTANT_TEXTS } from '@gp/constants/const';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

interface IStatusMeta {
  text: string;
  bgColor: string;
  color: string;
}

interface ITableRowProps extends IBaseProps {
  data: {
    business_owner: string;
    studio_cover: string;
    studio_id: string;
    studio_name: string;
    detail: IStudioOverallManagementBoardGovernanceMeetingData[];
  } | null;
}

const TAG_COLOR_MAP: Record<string, IStatusMeta> = {
  completed: {
    text: CONSTANT_TEXTS.COMPLETED,
    bgColor: '#ededef',
    color: '#6f7282',
  },
  upcoming: {
    text: 'Upcoming',
    bgColor: '#e6edfe',
    color: '#006EFF',
  },
};

const getStatusText = (payload?: number): 'Completed' | 'Upcoming' | '' => {
  if (payload === 1) return 'Completed';
  if (payload === 2) return 'Upcoming';
  return '';
};

const handleStudioClick = (
  e: React.MouseEvent<Element, MouseEvent> | null,
  studioId: string,
  studioName: string,
  queryJumpLink = false,
): string => {
  if (!studioId) return '';
  const filter = encodeURIComponent(
    JSON.stringify({
      studioId,
      studioName,
      menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'], // 用于标识左侧菜单栏当前选中高亮
      tab: 'management',
    }),
  );
  const url = `${SLASH_G_POP}/Studio/PortfolioOverview/Detail?filter=${filter}`;

  if (!queryJumpLink) {
    history.push(url);
  }

  return url;
};

const handleMeetingClick = (
  e: React.MouseEvent<HTMLElement, MouseEvent> | null,
  isNoJump: boolean,
  data: IStudioOverallManagementBoardGovernanceMeetingData,
  studioId: string,
  studioName: string,
  queryJumpLink = false,
): string => {
  if (isNoJump || !studioId) return '';
  const filter = encodeURIComponent(
    JSON.stringify({
      studioId,
      studioName,
      menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'], // 用于标识左侧菜单栏当前选中高亮
      tab: 'management',
      meetingId: data?.id, // 链接跳转时使用
    }),
  );
  const url = `${SLASH_G_POP}/Studio/PortfolioOverview/Detail?filter=${filter}`;
  if (!queryJumpLink) {
    history.push(url);
  }

  return url;
};

const TableRow: React.FC<ITableRowProps> = (props) => {
  const { style, className, data = null } = props;

  const {
    studio_id: studioId,
    studio_name: studioName,
    studio_cover: studioCover,
    business_owner: studioBusinessOwner,
    detail: studioDetails,
  } = data || {};

  const studioDetailPageUrl = handleStudioClick(null, studioId || '', studioName || '', true);

  const handleStudioClickPlus = (e: React.MouseEvent<Element, MouseEvent> | null) => {
    if (!queryJumpable(e)) return;
    handleStudioClick(e, studioId || '', studioName || '');
  };

  return (
    <div style={style} className={classnames('dbg-studio-overall-management-table-row-item', className)}>
      <div className="table-row-item-td studio-info-td">
        <LinkJump url={studioDetailPageUrl}>
          <GeneralImageNew
            style={{ border: '1px solid rgba(0, 0, 0, 0.1)' }}
            className={studioId && 'dbg-cursor-pointer'}
            domain="studio"
            width={44}
            height={44}
            src={studioCover || ''}
            onClick={handleStudioClickPlus}
          />
        </LinkJump>
        <div className="studio-info-td-right">
          <h3
            className={classnames('studio-info-td-right-title', { 'dbg-cursor-pointer': !!studioId })}
            title={studioName || '-'}
            onClick={handleStudioClickPlus}
          >
            <LinkJump style={{ color: '#222' }} stopPropagation={false} url={studioDetailPageUrl}>
              {studioName || '-'}
            </LinkJump>
          </h3>
          <p className="studio-info-td-right-info">
            BO: <span title={studioBusinessOwner || '-'}>{studioBusinessOwner || '-'}</span>
          </p>
        </div>
      </div>
      {Array.isArray(studioDetails) &&
        studioDetails.map((t: IStudioOverallManagementBoardGovernanceMeetingData, i) => {
          const isNoData = (!t?.date && !t?.name && ![1, 2]?.includes(t?.status)) || t?.status === 0;
          const tagText = getStatusText(t?.status);
          const isNoJump = isNoData || !t?.id;
          const url = handleMeetingClick(null, isNoJump, t, studioId || '', studioName || '', true);

          return (
            <div
              key={t?.id + i}
              style={{ padding: url ? 0 : '' }}
              className={classnames('table-row-item-td meeting-info-td', { 'hover-effect': !isNoJump })}
              onClick={(e) => {
                if (!queryJumpable(e)) return;
                handleMeetingClick(e, isNoJump, t, studioId || '', studioName || '');
              }}
            >
              <LinkJump style={{ display: 'block', padding: '20px 20px 16px 20px' }} stopPropagation={false} url={url}>
                {isNoData && <h3 className="meeting-info-td-title-text">No Data</h3>}
                {!isNoData && (
                  <>
                    <h3 className="meeting-info-td-title">
                      <span className="meeting-info-td-title-text" title={t?.name || '-'}>
                        {t?.name || '-'}
                      </span>
                      <Iconfont className="meeting-info-td-title-icon" type="title-arrow" />
                    </h3>
                    <div className="meeting-info-td-info">
                      <span className="td-info-date" title={t?.date?.slice(5, 10) || '-'}>
                        {t?.date?.slice(5, 10) || '-'}
                      </span>
                      {[1, 2]?.includes(t?.status) && (
                        <label
                          style={{
                            backgroundColor: TAG_COLOR_MAP?.[tagText?.toLowerCase()]?.bgColor,
                            color: TAG_COLOR_MAP?.[tagText?.toLowerCase()]?.color,
                          }}
                          className="td-info-tag"
                        >
                          <span className="td-info-tag-text">{tagText}</span>
                        </label>
                      )}
                    </div>
                  </>
                )}
              </LinkJump>
            </div>
          );
        })}
    </div>
  );
};

export default React.memo(TableRow);
